import { useEffect, useState } from "react";
import { Box, Flex, HStack, Text, IconButton, Spinner } from "@chakra-ui/react";
import { X } from "lucide-react";
import { Outlet } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { NavItems } from "@/components/NavItems";
import { Logo } from "@/components/Logo";
import { InstallPrompt, UpdateBanner } from "@/components/InstallPrompt";
import { OfflinePrefetch } from "@/components/OfflinePrefetch";
import { OutboxSync } from "@/components/OutboxSync";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { APP_VERSION } from "@/version";
import { bundleHash } from "@/lib/buildInfo";
import PendingAssignment from "@/pages/PendingAssignment";
import { colors } from "@/theme/colors";

const SIDEBAR_W = "264px";

export const AppLayout = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { role, isSuperAdmin, isAssigned, dataLoading } = useAuth();

  return (
    <Box bg={colors.page} minH="100vh">
      <UpdateBanner />
      <AppHeader onOpenMenu={() => setMenuOpen(true)} />
      <DesktopSidebar role={role} isSuperAdmin={isSuperAdmin} />
      <MobileDrawer open={menuOpen} onClose={() => setMenuOpen(false)} role={role} isSuperAdmin={isSuperAdmin} />

      {/* Main content */}
      <Box as="main" data-testid="app-main" ml={{ base: "0", lg: SIDEBAR_W }} pt="64px" minH="100vh">
        <Box p={{ base: "4", md: "6", lg: "8" }}>
          {dataLoading ? (
            <Flex minH="50vh" align="center" justify="center">
              <Spinner size="lg" color={colors.teal} borderWidth="3px" />
            </Flex>
          ) : isAssigned ? (
            <Outlet />
          ) : (
            <PendingAssignment />
          )}
        </Box>
      </Box>

      <InstallPrompt />
      <OutboxSync />
      <OfflinePrefetch />
    </Box>
  );
};

const DesktopSidebar = ({ role, isSuperAdmin }) => (
  <Box
    as="nav"
    data-testid="desktop-sidebar"
    display={{ base: "none", lg: "block" }}
    position="fixed"
    top="64px"
    left="0"
    bottom="0"
    w={SIDEBAR_W}
    bg={colors.white}
    borderRight="1px solid"
    borderColor={colors.border}
    overflowY="auto"
    py="3"
  >
    <NavItems role={role} isSuperAdmin={isSuperAdmin} />
    <SidebarFooter role={role} isSuperAdmin={isSuperAdmin} />
  </Box>
);

const MobileDrawer = ({ open, onClose, role, isSuperAdmin }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  return (
  <>
    <Box
      data-testid="mobile-drawer-overlay"
      display={{ base: open ? "block" : "none", lg: "none" }}
      position="fixed"
      inset="0"
      bg="rgba(31, 41, 55, 0.45)"
      zIndex={30}
      onClick={onClose}
    />
    <Box
      as="nav"
      data-testid="mobile-drawer"
      role="dialog"
      aria-modal={open ? "true" : undefined}
      aria-label="Navigation menu"
      aria-hidden={!open}
      display={{ base: "block", lg: "none" }}
      position="fixed"
      top="0"
      left="0"
      bottom="0"
      w="280px"
      bg={colors.white}
      zIndex={40}
      boxShadow="lg"
      transform={open ? "translateX(0)" : "translateX(-110%)"}
      transition="transform 0.25s ease"
      overflowY="auto"
    >
      <Flex h="64px" align="center" justify="space-between" px="4" borderBottom="1px solid" borderColor={colors.border}>
        <HStack gap="2.5">
          <Logo size={34} />
          <Text fontFamily="heading" fontWeight="700" fontSize="md">
            <Box as="span" color={colors.teal}>Hakina</Box>{" "}
            <Box as="span" color={colors.textPrimary}>Safety</Box>
          </Text>
        </HStack>
        <IconButton aria-label="Close menu" data-testid="close-menu-button" variant="ghost" size="sm" onClick={onClose} color={colors.textPrimary} _hover={{ bg: colors.cream }}>
          <X size={20} />
        </IconButton>
      </Flex>
      <NavItems role={role} isSuperAdmin={isSuperAdmin} onNavigate={onClose} />
      <SidebarFooter role={role} isSuperAdmin={isSuperAdmin} />
    </Box>
  </>
  );
};

const SidebarFooter = ({ role, isSuperAdmin }) => (
  <Box px="6" py="4" mt="2">
    <Text fontSize="xs" fontWeight="600" color={colors.textPrimary}>
      {isSuperAdmin ? ROLE_LABELS.super_admin : role ? ROLE_LABELS[role] : "Pending"}
    </Text>
    <Text fontSize="xs" color={colors.textSecondary} data-testid="app-version">
      Hakina Safety · v{APP_VERSION} · {bundleHash()}
    </Text>
  </Box>
);
